import { ATHENS_GRAY } from "@/controls/shared/constants/colors";
import { HeatNavigatorWithSearch } from "@/pages/heat-report/components/controls/HeatNavigatorWithSearch";
import { HeatData } from "@/pages/heat-report/models/HeatReportModels";
import { searchHeatNames } from "@/pages/meltshop/repositories/HeatRepository";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import {
	Box,
	Checkbox,
	FormControlLabel,
	Grid2 as Grid,
	Paper,
	Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LazyBackDropLoading } from "../../../../controls/LazyLoading";
import { Heat } from "../../chemistry-heat-report/models/Heat";
import { useMechanicalByHeatContext } from "./MechanicalByHeatContext";
import { TableMechanical } from "./components/TableMechanical";
import { useHeatNavigation } from "./hooks/useHeatNavigation";
import { useMechanicalData } from "./hooks/useMechanicalData";

export const MechanicalByHeatPage = () => {
	const { state, actions } = useMechanicalByHeatContext();
	const navigate = useNavigate();
	const { heatName: heatNameParam } = useParams();
	const [heatName, setHeatName] = useState(heatNameParam || null);
	const [error, setError] = useState<string | null>(null);
	const [isLoadingHeat, setIsLoadingHeat] = useState(true);
	const { handleValidSamples } = useHeatNavigation();
	const { getCatalogData, getSampleData } = useMechanicalData();

	useEffect(() => {
		getCatalogData();
	}, [getCatalogData]);

	useEffect(() => {
		getSampleData();
	}, [getSampleData]);

	// Load heat on initial mount or when URL param changes
	useEffect(() => {
		const loadInitialHeat = async () => {
			setIsLoadingHeat(true);
			try {
				if (heatNameParam) {
					// Skip if we already have the correct heat loaded
					if (state.heat?.HeatName === heatNameParam) {
						setIsLoadingHeat(false);
						return;
					}
					// Load heat by name from URL param
					const resp = await searchHeatNames({
						searchText: heatNameParam,
						IncludeIsExternal: true,
					});
					if (resp.ok) {
						const heatFound = resp.data.tables[0].rows.find(
							(h: Heat) => h.HeatName === heatNameParam,
						);
						if (heatFound) {
							actions.setHeat(heatFound);
							setHeatName(heatFound.HeatName);
						}
					}
				} else {
					// No URL param - load last heat
					const resp = await searchHeatNames({
						searchText: "",
						IncludeIsExternal: true,
					});
					if (resp.ok && resp.data.tables[0].rows.length > 0) {
						const lastHeat = resp.data.tables[0].rows[0];
						actions.setHeat(lastHeat);
						setHeatName(lastHeat.HeatName);
						navigate(`/lab-mechanical-by-heat/${lastHeat.HeatName}`, {
							replace: true,
						});
					}
				}
			} catch (error) {
				setError("Error loading heat: " + error);
			} finally {
				setIsLoadingHeat(false);
			}
		};

		loadInitialHeat();
	}, [heatNameParam]);

	// Convert Heat to HeatData for HeatNavigatorWithSearch
	const heatNavigatorData: HeatData = useMemo(() => {
		return {
			HeatId: state.heat?.HeatId ?? 0,
			HeatName: state.heat?.HeatName ?? "",
			NextHeatId: state.heat?.NextHeatId ?? null,
			PreviousHeatId: state.heat?.PreviousHeatId ?? null,
			NextHeatName: state.heat?.NextHeatName ?? null,
			PreviousHeatName: state.heat?.PreviousHeatName ?? null,
			Grade: "",
			LadleName: null,
			HeatsOnLadle: null,
			TapDate: null,
			TapShift: null,
			TapCrew: null,
			OpenDate: null,
			OpenShift: null,
			OpenCrew: null,
			FinalStatus: null,
			SteelWeight: null,
			EAFProcessId: null,
			CASProcessId: null,
		};
	}, [state.heat]);

	const fetchAndSetHeat = useCallback(
		async (heatNameToSearch: string) => {
			try {
				const resp = await searchHeatNames({
					searchText: heatNameToSearch,
					IncludeIsExternal: true,
				});
				if (resp.ok) {
					const heatFound = resp.data.tables[0].rows.find(
						(h: Heat) => h.HeatName === heatNameToSearch,
					);
					if (heatFound) {
						actions.setHeat(heatFound);
						setHeatName(heatFound.HeatName);
						navigate(`/lab-mechanical-by-heat/${heatFound.HeatName}`);
					}
				}
			} catch (err) {
				setError("Error fetching heat: " + err);
			}
		},
		[actions, navigate],
	);

	const handleNext = useCallback(() => {
		if (state.heat?.NextHeatName) {
			fetchAndSetHeat(state.heat.NextHeatName);
		}
	}, [state.heat?.NextHeatName, fetchAndSetHeat]);

	const handlePrevious = useCallback(() => {
		if (state.heat?.PreviousHeatName) {
			fetchAndSetHeat(state.heat.PreviousHeatName);
		}
	}, [state.heat?.PreviousHeatName, fetchAndSetHeat]);

	const handleLast = useCallback(() => {
		// Navigate to base URL - useEffect will load the last heat
		actions.setHeat(null);
		navigate("/lab-mechanical-by-heat/");
	}, [actions, navigate]);

	const handleHeatSelect = useCallback(
		async (heatId: number) => {
			try {
				// Use heatId parameter to search by ID directly
				const resp = await searchHeatNames({
					searchText: "",
					heatId: heatId,
					IncludeIsExternal: true,
				});
				if (resp.ok && resp.data.tables[0].rows.length > 0) {
					const heatFound = resp.data.tables[0].rows[0];
					actions.setHeat(heatFound);
					setHeatName(heatFound.HeatName);
					navigate(`/lab-mechanical-by-heat/${heatFound.HeatName}`);
				}
			} catch (err) {
				setError("Error fetching heat: " + err);
			}
		},
		[actions, navigate],
	);

	// Create stable array references to prevent infinite loops
	// The ?? [] operator creates NEW arrays on every render, causing AG Grid to detect changes
	const stableMechanicalTests = useMemo(
		() => state.mechanicalTests ?? [],
		[state.mechanicalTests],
	);
	const stableMechanicalSamples = useMemo(
		() => state.mechanicalSamples ?? [],
		[state.mechanicalSamples],
	);
	const stableGradeLimits = useMemo(() => state.grades ?? [], [state.grades]);
	const stableResultSources = useMemo(
		() => state.sources ?? [],
		[state.sources],
	);

	return (
		<>
			<HelmetDexteel title={`Mechanical Test`} />
			<Paper
				elevation={0}
				style={{
					width: "100%",
					padding: "15px 20px 10px",
					height: "93vh",
					backgroundColor: ATHENS_GRAY,
				}}
			>
				<LazyBackDropLoading open={state.isLoadingBackground} />
				<Grid container justifyContent="center" alignItems="center">
					<Grid size={{ xs: 12, md: 12 }} style={{ margin: 0 }}>
						<Grid size={{ xs: 12, md: 12 }}>
							<Typography variant="h5" style={{ fontWeight: 600 }}>
								Mechanical Test
							</Typography>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }} style={{ marginBottom: "10px" }}>
							<Grid
								container
								direction="row"
								justifyContent="flex-start"
								alignItems="stretch"
								spacing={1}
							>
								<Grid size={{ xs: "auto" }} style={{ display: "flex" }}>
									<HeatNavigatorWithSearch
										value={heatNavigatorData}
										onChange={handleHeatSelect}
										onBlur={() => {}}
										elevation={1}
										isProcessLoading={isLoadingHeat}
										showNext={!isLoadingHeat && !!state.heat?.NextHeatId}
										showPrevious={
											!isLoadingHeat && !!state.heat?.PreviousHeatId
										}
										onNext={handleNext}
										onPrevious={handlePrevious}
										onLast={handleLast}
									/>
								</Grid>
								<Grid size={{ xs: "auto" }} style={{ display: "flex" }}>
									<Paper
										elevation={1}
										style={{
											minWidth: "300px",
											display: "flex",
											alignItems: "center",
										}}
									>
										<Box sx={{ p: 2 }}>
											<Grid
												container
												spacing={1}
												justifyContent="flex-start"
												alignItems="center"
											>
												<Grid size={{ xs: 12 }}>
													<FormControlLabel
														control={
															<Checkbox
																color="primary"
																checked={state.showOnlyValid}
																onChange={(e) =>
																	handleValidSamples(e.target.checked)
																}
															/>
														}
														label="Show valid samples"
													/>
												</Grid>
											</Grid>
										</Box>
									</Paper>
								</Grid>
							</Grid>
						</Grid>
						<TableMechanical
							loadingGrid={state.isLoadingGrid}
							heat={state.heat}
							mechanicalTests={stableMechanicalTests}
							mechanicalSamples={stableMechanicalSamples}
							gradeLimits={stableGradeLimits}
							resultSources={stableResultSources}
						/>
					</Grid>
				</Grid>
			</Paper>
			<ErrorModal error={error} onHide={() => setError(null)} />
		</>
	);
};
